import type { Node, Edge } from "reactflow";
import type { CanvasEdge, CanvasNode } from "@opus-dt/shared-types";
import type { MachineDetail } from "@/features/lines/hooks";

export function toRfNode(
  n: CanvasNode,
  machineById: Map<string, MachineDetail>,
  isReadonly: boolean
): Node {
  const linkedMachine =
    n.type === "MACHINE" && typeof n.data?.machineId === "string"
      ? machineById.get(n.data.machineId)
      : undefined;
  return {
    id: n.id,
    type: n.type,
    position: { x: n.x, y: n.y },
    data: {
      ...n.data,
      label: n.label,
      status: linkedMachine?.status,
      // Badge de operador no nó (edit e draft via machineById).
      requiresOperator: linkedMachine?.requiresOperator,
      // Badge de contador de produção (display + draft via machineById).
      hasProductionCounter: linkedMachine?.parameters?.some((p) => p.productionRole === "PRODUCES"),
      machineId: n.type === "MACHINE" ? (n.data?.machineId as string | undefined) : undefined,
    },
    draggable: !isReadonly,
    selectable: true,
    connectable: !isReadonly,
    zIndex: n.type === "GROUP" ? -1 : 0,
  };
}

export function toRfEdge(e: CanvasEdge): Edge {
  return {
    id: e.id,
    source: e.sourceNodeId,
    target: e.targetNodeId,
    type: e.type,
    data: e.data,
  };
}

export function fromRfNode(n: Node): CanvasNode {
  return {
    id: n.id,
    type: (n.type as CanvasNode["type"]) ?? "EQUIPMENT",
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React Flow Node.data tipado como Record<string, unknown>; cast inline preserva contrato terceiro
    label: (n.data?.label as string) ?? "",
    x: n.position.x,
    y: n.position.y,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- n.data eh Record<string, unknown> por contrato React Flow
    data: stripUiFields(n.data ?? {}),
  };
}

export function fromRfEdge(e: Edge): CanvasEdge {
  return {
    id: e.id,
    sourceNodeId: e.source,
    targetNodeId: e.target,
    type: (e.type as CanvasEdge["type"]) ?? "PRODUCTION_FLOW",
    data: (e.data as Record<string, unknown>) ?? {},
  };
}

export function stripUiFields(data: Record<string, unknown>): Record<string, unknown> {
  // requiresOperator é injetado por toRfNode — UI-only, igual a label/status:
  // nunca persiste no data do nó (line_layout_node.data).
  const { label, status, requiresOperator, hasProductionCounter, ...rest } = data;
  void label;
  void status;
  void requiresOperator;
  void hasProductionCounter;
  return rest;
}

export function labelOf(n: Node): string {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- React Flow Node.data tipado como Record<string, unknown>; cast inline preserva contrato terceiro
  return (n.data?.label as string) ?? n.id;
}
